import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { datadogLogs } from '@datadog/browser-logs';
import { ENV_CONFIG } from 'config/environment';
import { getMessageFromAxiosError } from 'helpers/errors';
import API_ROUTES from 'services/apiRoutes';
import { getSiteAccessToken } from 'services/storage';
import { initialState } from './initialState';
import type { UserChatRequest, UserChatResponse } from './types';

const headers = {
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': '*',
};

export const postUserChat = createAsyncThunk<
  UserChatResponse,
  UserChatRequest,
  { rejectValue: string }
>('userChat/post', async ({ prompt, conversation_id, context_history }, { rejectWithValue }) => {
  const url = `${ENV_CONFIG().API.BASE_URL}${API_ROUTES.USER_CHAT}`;

  try {
    const response = await axios.post(
      url,
      { prompt, conversation_id, context_history },
      { headers: { ...headers, Authorization: `Bearer ${getSiteAccessToken()}` } }
    );
    return response.data as UserChatResponse;
  } catch (error: any) {
    datadogLogs.logger.warn(
      'Error occurred posting user chat',
      {},
      new Error(getMessageFromAxiosError(error))
    );
    return rejectWithValue(error.message);
  }
});

export const userChatSliceReducer = createSlice({
  name: 'userChat',
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: builder => {
    builder
      .addCase(postUserChat.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.data = null;
      })
      .addCase(postUserChat.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(postUserChat.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to get a chat response';
      });
  },
});

export const { clear: clearUserChat } = userChatSliceReducer.actions;
export default userChatSliceReducer.reducer;
